

import React, { useEffect, useState } from "react";
import { Button, Dropdown, InputNumber, Menu, Slider, Switch, Tooltip } from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styles from './ChatSettingsButton.module.scss'
import { ChatSettings } from "../../../../libs/ui-settings-manager/interfaces";
import { UISettingsManager } from "../../../../libs/ui-settings-manager/UISettingsManager";
import { DEFAULT_ARCHIVE_DAYS, MAX_ARCHIVE_DAYS, MIN_ARCHIVE_DAYS } from "../../../../constants";

interface ChatSettingsButtonProps {
    style?: React.CSSProperties;
}

const ChatSettingsButton: React.FC<ChatSettingsButtonProps> = ({ style }) => {
    const [settings, setSettings] = useState<ChatSettings>(UISettingsManager.getSettings().chat);
    const { t } = useTranslation();
    const baseT = (value: string) => t('chat.settings.' + value);

    useEffect(() => {
        const handleSettingsChange = () => {
            const uiSettings = UISettingsManager.getSettings();
            setSettings(uiSettings.chat);
        };

        window.addEventListener('uiSettingsChanged', handleSettingsChange);
        return () => window.removeEventListener('uiSettingsChanged', handleSettingsChange);
    }, []);

    const handleOpacityChange = (value: number) => {
        UISettingsManager.updateSection('chat', {
            opacity: value / 100
        });
    };

    const settingsMenu = (
        <Menu className={styles.settingsContainer}>
            <Menu.ItemGroup title={baseT('chatSettings')}>
                <Menu.Item key="chatPosition">
                    <div className={styles.settingsGroup} onClick={(e) => e.stopPropagation()}>
                        <span style={{ marginRight: 12 }}>{baseT('placementChat')}</span>
                        <div className={styles.settingsGroupRow}>
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.chatPosition === 'top' ? '#1890ff' : '#999'}}>
                                {baseT('top')}
                            </span>
                            <Switch
                                size="small"
                                checked={settings.chatPosition === 'bottom'}
                                onChange={(checked) => {
                                    UISettingsManager.updateSection('chat', {
                                        chatPosition: checked ? 'bottom' : 'top'
                                    });
                                }}
                            />
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.chatPosition === 'bottom' ? '#999' : '#999'}}>
                                {baseT('bottom')}
                            </span>
                        </div>
                    </div>
                </Menu.Item>

                <Menu.Item key="buttonPosition">
                    <div className={styles.settingsGroup} onClick={(e) => e.stopPropagation()}>
                        <span style={{ marginRight: 12 }}>{baseT('placementButton')}</span>
                        <div className={styles.settingsGroupRow}>
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.buttonPosition === 'top' ? '#1890ff' : '#999'}}>
                                {baseT('top')}
                            </span>
                            <Switch
                                size="small"
                                checked={settings.buttonPosition === 'bottom'}
                                onChange={(checked) => {
                                    UISettingsManager.updateSection('chat', {
                                        buttonPosition: checked ? 'bottom' : 'top'
                                    });
                                }}
                            />
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.buttonPosition === 'bottom' ? '#999' : '#999'}}>
                                {baseT('bottom')}
                            </span>
                        </div>
                    </div>
                </Menu.Item>

                <Menu.Item key="archiveDays">
                    <div className={styles.settingsGroup} onClick={(e) => e.stopPropagation()}>
                        <span style={{ marginRight: 12 }}>{baseT('archiveDays')}</span>
                        <Tooltip
                            title={`Минимум ${MIN_ARCHIVE_DAYS}, максимум ${MAX_ARCHIVE_DAYS} дней`}
                            getPopupContainer={() => document.body}
                            placement="top"
                        >
                            <InputNumber
                                size="small"
                                min={MIN_ARCHIVE_DAYS}
                                max={MAX_ARCHIVE_DAYS}
                                value={settings.archiveDays || DEFAULT_ARCHIVE_DAYS}
                                onChange={(value) => {
                                    UISettingsManager.updateSection('chat', {
                                        archiveDays: value || DEFAULT_ARCHIVE_DAYS
                                    });
                                }}
                                style={{ width: 60 }}
                            />
                        </Tooltip>
                        <span style={{ marginRight: 12 }}>{baseT('days')}</span>
                    </div>
                </Menu.Item>

                <Menu.Item key="sound">
                    <div className={styles.settingsGroup} onClick={(e) => e.stopPropagation()}>
                        <span style={{ marginRight: 12 }}>{baseT('sound')}</span>
                        <div className={styles.settingsGroupRow}>
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.sound === true ? '#1890ff' : '#999'}}>
                                {baseT('off')}
                            </span>
                            <Switch
                                size="small"
                                checked={settings.sound}
                                onChange={(checked) => {
                                    UISettingsManager.updateSection('chat', {
                                        sound: checked ? true : false
                                    });
                                }}
                            />
                            <span className={styles.settingsGroupRowTitle} style={{color: settings.sound === false ? '#999' : '#999'}}>
                                {baseT('on')}
                            </span>
                        </div>
                    </div>
                </Menu.Item>
                
                <Menu.Item key="opacity">
                    <div
                        className={styles.settingsGroupSlider}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className={styles.settingsGroupSliderAreaWrapper}>
                            <span>{baseT('opacity')}</span>
                            <span className={styles.settingsGroupSliderAreaValue}>
                                {Math.round(settings.opacity * 100)}%
                            </span>
                        </div>
                        <Slider
                            className={styles.opacitySlider}
                            min={30}
                            max={100}
                            step={5}
                            value={settings.opacity * 100}
                            onChange={handleOpacityChange}
                            tooltip={{
                                formatter: (value) => `${value}%`
                            }}
                        />
                        <div className={styles.opacitySliderLegend}>
                            <span>30%</span>
                            <span>100%</span>
                        </div>
                    </div>
                </Menu.Item>
            </Menu.ItemGroup>
            <Menu.Divider />
            <Menu.Item
                className={styles.settingsResetButton}
                key="resetAlerts"
            >
                <div
                    className={styles.settingsResetButtonGroup}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        UISettingsManager.resetSection('chat');
                    }}
                >
                    {baseT('resetChatSettings')}
                </div>
            </Menu.Item>
        </Menu>
    );

    return (
        <Dropdown
            overlay={settingsMenu}
            placement="bottomRight"
            trigger={['click']}
            overlayStyle={{ zIndex: 1060 }}
            getPopupContainer={() => document.body}
        >
            <Tooltip
                title={baseT('chatSettings')}
                placement="top"
                getPopupContainer={() => document.body}
            >
                <Button
                    className={styles.settingsButton}
                    type="text"
                    size="small"
                    icon={<SettingOutlined />}
                />
            </Tooltip>
        </Dropdown>
    );
};

export default ChatSettingsButton;